<ion-header>
    <ion-toolbar>
        <ion-buttons slot="start">
            <ion-button (click)="cancel()">Cancel</ion-button>
        </ion-buttons>
        <ion-title>{{ editId() ? 'Edit' : 'New' }} Calculated Signal</ion-title>
        <ion-buttons slot="end">
            <ion-button (click)="save()" [disabled]="parserError() || duplicateNameError() || !formula.trim() || !name.trim()">
                <strong>Save</strong>
            </ion-button>
        </ion-buttons>
    </ion-toolbar>
</ion-header>

<ion-content>
    <ion-list>
        <ion-item>
            <ion-label position="stacked">Name</ion-label>
            <ion-input [(ngModel)]="name" (ngModelChange)="onNameChange()" placeholder="e.g. Total Horsepower"></ion-input>
        </ion-item>

        <ion-item>
            <ion-row style="width: 100%">
                <ion-col size="6" style="padding-left: 0">
                    <ion-label position="stacked">Unit Label</ion-label>
                    <ion-input [(ngModel)]="unit" placeholder="e.g. HP"></ion-input>
                </ion-col>
                <ion-col size="6" style="padding-right: 0">
                    <ion-label position="stacked">Precision</ion-label>
                    <ion-input type="number" [(ngModel)]="precision" min="0" max="6"></ion-input>
                </ion-col>
            </ion-row>
        </ion-item>

        <ion-item lines="none">
            <ion-label position="stacked" style="margin-bottom: 4px">Formula Composer</ion-label>
            <ion-textarea
                [(ngModel)]="formula"
                (ngModelChange)="onFormulaChange()"
                placeholder="Type or tap below to build expression..."
                rows="3"
                style="border: 1px solid var(--ion-color-step-300, #b3b3b3); border-radius: 8px; padding: 8px"
            ></ion-textarea>
        </ion-item>

        <!-- Validation / Preview Box -->
        <div style="padding: 0 16px; height: 50px; overflow-y: auto">
            @if (duplicateNameError()) {
                <div class="error-box" style="margin-bottom: 8px">
                    <ion-text color="danger">
                        <ion-icon name="warning" style="vertical-align: middle"></ion-icon>
                        {{ duplicateNameError() }}
                    </ion-text>
                </div>
            }
            @if (parserError()) {
                <div class="error-box">
                    <ion-text color="danger">
                        <ion-icon name="warning" style="vertical-align: middle"></ion-icon>
                        Syntax Error: {{ parserError() }}
                    </ion-text>
                </div>
            }
            @if (!parserError() && formula.trim()) {
                <div class="success-box">
                    <ion-text color="success"> <strong>Preview: </strong> {{ previewResult() }} </ion-text>
                </div>
            }
        </div>

        <div style="height: 4px"></div>
        <!-- spacer -->

        <!-- Touch Keypad Section -->
        <div style="padding: 0 8px">
            <ion-grid>
                <!-- Operators Row -->
                <ion-row>
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText(' + ')">+</ion-button></ion-col
                    >
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText(' - ')">-</ion-button></ion-col
                    >
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText(' * ')">*</ion-button></ion-col
                    >
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText(' / ')">/</ion-button></ion-col
                    >
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText('(')">(</ion-button></ion-col
                    >
                    <ion-col size="2"
                        ><ion-button expand="block" color="light" class="keypad-btn" (click)="insertText(')')">)</ion-button></ion-col
                    >
                </ion-row>

                <!-- Modal Triggers for Vars vs Functions -->
                <ion-row style="margin-top: 8px">
                    <ion-col size="6">
                        <ion-button expand="block" id="btn-variables" color="dark" fill="outline"
                            >Variables <ion-icon slot="end" name="chevron-down-outline"></ion-icon
                        ></ion-button>
                        <ion-modal trigger="btn-variables" id="var-modal">
                            <ng-template>
                                <ion-header>
                                    <ion-toolbar>
                                        <ion-title>Select Variable</ion-title>
                                    </ion-toolbar>
                                </ion-header>
                                <ion-content>
                                    <ion-list>
                                        @for (sig of availableSignals; track sig.key) {
                                            <ion-item button (click)="insertText(sig.key, 'var-modal')">
                                                <ion-label class="popover-btn-label">
                                                    <strong>{{ sig.name }}</strong
                                                    ><br />
                                                    <ion-note style="font-family: monospace">{{ sig.key }}</ion-note>
                                                </ion-label>
                                            </ion-item>
                                        } @empty {
                                            <ion-item>
                                                <ion-label color="medium" style="white-space: normal"
                                                    >No signals found. Load a log file to extract variables.</ion-label
                                                >
                                            </ion-item>
                                        }
                                    </ion-list>
                                </ion-content>
                            </ng-template>
                        </ion-modal>
                    </ion-col>

                    <ion-col size="6">
                        <ion-button expand="block" id="btn-functions" color="primary" fill="outline"
                            >Functions <ion-icon slot="end" name="chevron-down-outline"></ion-icon
                        ></ion-button>
                        <ion-modal trigger="btn-functions" id="fn-modal">
                            <ng-template>
                                <ion-header>
                                    <ion-toolbar>
                                        <ion-title>Select Function</ion-title>
                                    </ion-toolbar>
                                </ion-header>
                                <ion-content>
                                    <ion-list>
                                        @for (fn of standardFunctions; track fn.insert) {
                                            <ion-item button (click)="insertText(fn.insert, 'fn-modal')">
                                                <ion-label class="popover-btn-label">
                                                    <strong>{{ fn.name }}</strong
                                                    ><br />
                                                    <ion-note style="font-family: monospace">{{ fn.desc }}</ion-note>
                                                </ion-label>
                                            </ion-item>
                                        }
                                    </ion-list>
                                </ion-content>
                            </ng-template>
                        </ion-modal>
                    </ion-col>
                </ion-row>
            </ion-grid>
        </div>
    </ion-list>
</ion-content>
